import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Injector, OnInit, signal } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { CBox_PublicGetReportFileResponse, CBox_PublicGetReportListDataParams, CBox_PublicGetReportListItemData, CBox_PublicReportFileTypes, CBox_PublicReportType } from "@server/services/cbox/public/api/v1/resources/reports/types";
import { ButtonModule } from "primeng/button";
import { CalendarModule } from "primeng/calendar";
import { CheckboxModule } from "primeng/checkbox";
import { DropdownModule } from "primeng/dropdown";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { MenuModule } from "primeng/menu";
import { MultiSelectModule } from "primeng/multiselect";
import { ProgressBarModule } from "primeng/progressbar";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { TableModule } from "primeng/table";
import { first } from "rxjs";
import { handlePublicApiError } from "src/services/api/api.service";
import { ProfileService } from "src/services/profile/profile.service";
import { fileDownloadHelper } from "src/helpers/file-download";
import { CBoxProfileReportGenerateControllerComponent } from "../dialogs/generate/controller/cbox-profile-report-generate-controller.component";
import { CBoxProfileAgentsActivityReportGenerateDialogComponent } from "../dialogs/generate/agents-activity/cbox-profile-agents-activity-report-generate-dialog.component";
import { CBoxProfileLockersActivityReportGenerateDialogComponent } from "../dialogs/generate/lockers-activity/cbox-profile-lockers-activity-report-generate-dialog.component";
import { PaginatedTableWithFilters } from "app/shared/paginated-table-with-filters";
import { CBox_PublicSuccessResponse } from "@server/services/cbox/public/api/v1/resources/common/request_base/types";
import { formatDate, removeDiacritics } from "src/helpers/functions";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TagModule } from "primeng/tag";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { CBoxProfileLockerDataComponent } from "app/profile/lockers/data/cbox-profile-locker-data.component";
import { InputTextModule } from "primeng/inputtext";
import { MenuItem } from "primeng/api";
import { CBoxProfileShipmentActivityReportGenerateDialogComponent } from "../dialogs/generate/shipment-activity/cbox-profile-shipment-activity-report-generate-dialog.component";
import { CBoxProfilePickUpTimesActivityReportGenerateDialogComponent } from "../dialogs/generate/pick-up-times-activity/cbox-profile-pick-up-times-activity-report-generate-dialog.component";
import { CBoxProfileLockerLowActivityReportGenerateDialogComponent } from "../dialogs/generate/low-activity/cbox-profile-locker-low-activity-report-generate-dialog.component";

type ReportTranslations = Partial<
  Record<CBox_PublicReportType, Partial<
    Record<CBox_PublicReportFileTypes, Record<string, string>>
  >>
>;

@Component({
  selector: "app-cbox-profile-generated-reports",
  templateUrl: "./cbox-profile-generated-reports.component.html",
  styleUrls: ["./cbox-profile-generated-reports.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DropdownModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    CalendarModule,
    CheckboxModule,
    MultiSelectModule,
    TableModule,
    MenuModule,
    ProgressBarModule,
    ProgressSpinnerModule,
    OverlayPanelModule,
    InputTextModule,
    TagModule
  ]
})

export class CBoxProfileGeneratedReportsComponent extends PaginatedTableWithFilters<CBox_PublicGetReportListItemData, CBox_PublicGetReportListDataParams> implements OnInit {
  filtersForm: FormGroup;
  reports = signal<CBox_PublicGetReportListItemData[]>([]);
  reportTypeTranslations = signal<Record<string, string>>({
    [CBox_PublicReportType.agentActivity]: "Activitate agenți",
    [CBox_PublicReportType.lockerActivity]: "Activitate lockere",
    [CBox_PublicReportType.shipmentActivity]: "Activitate trimiteri",
    [CBox_PublicReportType.pickUpTimesActivity]: "Timpi de ridicare",
    [CBox_PublicReportType.lockerLowActivity]: "Lockere subutilizate"
  });
  reportTypes = signal<{
    type: CBox_PublicReportType,
    label: string,
  }[]>([
    {
      type: CBox_PublicReportType.agentActivity,
      label: this.reportTypeTranslations()[CBox_PublicReportType.agentActivity]
    },
    {
      type: CBox_PublicReportType.lockerActivity,
      label: this.reportTypeTranslations()[CBox_PublicReportType.lockerActivity]
    },
    {
      type: CBox_PublicReportType.shipmentActivity,
      label: this.reportTypeTranslations()[CBox_PublicReportType.shipmentActivity]
    },
    {
      type: CBox_PublicReportType.pickUpTimesActivity,
      label: this.reportTypeTranslations()[CBox_PublicReportType.pickUpTimesActivity]
    },
    {
      type: CBox_PublicReportType.lockerLowActivity,
      label: this.reportTypeTranslations()[CBox_PublicReportType.lockerLowActivity]
    }
  ]);
  reportStatesTranslations = signal<Record<string, string>>({
    PENDING: "În așteptare",
    IN_PROGRESS: "In generare",
    PENDING_RETRY: "În așteptare",
    FINISHED: "Finalizat",
    ERRORED: "Eroare",
    CANCELED: "Anulat"
  });
  reportStates = signal<{
    state: string,
    label: string,
    severity: "success" | "secondary" | "info" | "warning" | "contrast" | "danger" | undefined
  }[]>([
    {
      state: "PENDING",
      label: "În așteptare",
      severity: "secondary"
    },
    {
      state: "IN_PROGRESS",
      label: "In generare",
      severity: "info"
    },
    {
      state: "PENDING_RETRY",
      label: "În așteptare",
      severity: "info"
    },
    {
      state: "FINISHED",
      label: "Finalizat",
      severity: "success"
    },
    {
      state: "ERRORED",
      label: "Eroare",
      severity: "danger"
    },
    {
      state: "CANCELED",
      label: "Anulat",
      severity: "danger"
    }
  ]);
  downloadingItems = signal<{ identifier?: string, type?: CBox_PublicReportFileTypes }[]>([]);
  dataFetchUrl = "reports";
  sessionFiltersKey = "profile/reports/generated/filters";
  sessionFiltersToSave = ["page", "pageSize", "interval", "types", "agents"];
  localStorageFiltersKey = "profile/reports/generated/filters";
  localStorageFiltersToSave = [];
  defaultFilters = signal({
    page: 1,
    pageSize: 25,
    interval: [this.oneMonthAgo, this.today],
    types: [
      CBox_PublicReportType.agentActivity,
      CBox_PublicReportType.lockerActivity,
      CBox_PublicReportType.shipmentActivity,
      CBox_PublicReportType.pickUpTimesActivity,
      CBox_PublicReportType.lockerLowActivity
    ],
    agents: []
  });
  overlayPanelSearchFilter = new FormControl("");
  overlayPanelLockersFilterOptions = signal<CBox_PublicGetReportListItemData["lockers"]>([]);
  filteredOverlayPanelLockersOptions = signal<CBox_PublicGetReportListItemData["lockers"]>([]);
  overlayPanelAgentsFilterOptions = signal<CBox_PublicGetReportListItemData["agents"]>([]);
  filteredOverlayPanelAgentsOptions = signal<CBox_PublicGetReportListItemData["agents"]>([]);
  reportFileTypes = CBox_PublicReportFileTypes;
  reportVariationsTranslations = signal<ReportTranslations>({
    [CBox_PublicReportType.lockerActivity]: {
      [CBox_PublicReportFileTypes.PDF]: {
        default: "Raport complet",
        short: "Rezumat lockere",
        summary: "Rezumat periodă"
      }
    },
    [CBox_PublicReportType.pickUpTimesActivity]: {
      [CBox_PublicReportFileTypes.PDF]: {
        default: "Raport complet",
        short: "Sumar"
      }
    },
    [CBox_PublicReportType.lockerLowActivity]: {
      [CBox_PublicReportFileTypes.PDF]: {
        default: "Raport complet"
      }
    }
  });

  constructor(
    inj: Injector,
    private profileService: ProfileService,
    private fb: FormBuilder,
    private dialog: MatDialog) {
      super(inj);
      this.filtersForm = this.fb.group({
        page: [1],
        pageSize: [25],
        interval: [[this.oneMonthAgo, this.today]],
        types: [[0]],
        agents: [[CBox_PublicReportType.agentActivity, CBox_PublicReportType.lockerActivity]]
      });
    }

  ngOnInit(): void {
    this.reportsService.onUpdate().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.fetchData();
    });
  }

  async init(): Promise<void> {
    this.profileService.setTitle("Rapoarte generate");

    this.overlayPanelSearchFilter.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((searchString) => {
          const normalizedSearch =
              removeDiacritics(searchString?.toLowerCase() ?? '');

          // Filter lockers options
          const lockersOptions = this.overlayPanelLockersFilterOptions();
          const filteredLockers = lockersOptions.filter((locker) => {
            const normalizedName =
                removeDiacritics(locker.name || '').toLowerCase();
            const normalizedId = locker.id.toLowerCase();
            return normalizedName.includes(normalizedSearch) ||
                normalizedId.includes(normalizedSearch);
          });
          this.filteredOverlayPanelLockersOptions.set(filteredLockers);

          // Filter agents options
          const agentsOptions = this.overlayPanelAgentsFilterOptions();
          const filteredAgents = agentsOptions.filter((agent) => {
            const normalizedName =
                removeDiacritics(agent.name || '').toLowerCase();
            const normalizedIdentifier = agent.identifier.toLowerCase();
            return normalizedName.includes(normalizedSearch) ||
                normalizedIdentifier.includes(normalizedSearch);
          });
          this.filteredOverlayPanelAgentsOptions.set(filteredAgents);
        });
  }

  downloadReport(report: CBox_PublicGetReportListItemData, type: CBox_PublicReportFileTypes, variation: string): void {
    this.downloadingItems.update(items => {
      items.push({
        identifier: report.identifier,
        type: type
      });
      return items;
    });

    this.api
        .get<CBox_PublicSuccessResponse<CBox_PublicGetReportFileResponse>>(
            `backend/reports/${report.identifier}/file?fileType=${type}&variationName=${variation}`)
        .subscribe(
            (response) => {
              let type: string;
              switch (report.type) {
                case CBox_PublicReportType.lockerActivity:
                  if (variation === "default") {
                    type = "raport-activitate-lockere";
                  } else if (variation === "shory") {
                    type = "rezumat-lockere";
                  } else if (variation === "summary") {
                    type = "rezumat-perioadă";
                  }
                  break;

                case CBox_PublicReportType.agentActivity:
                  type = "raport-activitate-agenți";
                  break;

                case CBox_PublicReportType.shipmentActivity:
                  type = "raport-activitate-trimiteri";
                  break;

                case CBox_PublicReportType.pickUpTimesActivity:
                  type = "raport-timpi-de-ridicare";
                  break;

                case CBox_PublicReportType.lockerLowActivity:
                  type = "raport-lockere-activitate-scazuta";
                  break;


                default:
                  type = "raport";
                  break;
              }
              const filename = `${formatDate(new Date(report.interval.start))}_${formatDate(new Date(report.interval.end))}_${type!}`;
              fileDownloadHelper(
                  response.data.content as any,
                  response.data.type.toLowerCase(),
                  filename + "." + response.data.type.toLowerCase());
              this.toastService.showSuccessToast(
                  "Confirmare", "Raportul a fost descărcat cu succes");
              this.downloadingItems.update(
                  items => items.filter(
                      report => report.identifier !== report.identifier));
            },
            (e: HttpErrorResponse) => {
              handlePublicApiError(e, this.toastService);
              this.downloadingItems.update(
                  items => items.filter(
                      report => report.identifier !== report.identifier));
            });
  }

  patchLockerFilterOptions(lockers: CBox_PublicGetReportListItemData["lockers"]): void {
    this.overlayPanelLockersFilterOptions.set(lockers);
    this.filteredOverlayPanelLockersOptions.set(lockers);
  }

  patchAgentFilterOptions(agents: CBox_PublicGetReportListItemData["agents"]): void {
    this.overlayPanelAgentsFilterOptions.set(agents);
    this.filteredOverlayPanelAgentsOptions.set(agents);
  }

  isDownloading(report: CBox_PublicGetReportListItemData, type: CBox_PublicReportFileTypes): boolean {
    return !!this.downloadingItems()?.find(item => item.identifier === report.identifier && item.type === type);
  }

  getReportSeverity(state: string): "success" | "secondary" | "info" | "warning" | "contrast" | "danger" | undefined {
    const reportState = this.reportStates()?.find(reportState => reportState.state === state);
    return reportState?.severity || "secondary";
  }

  getReportFileTypeCount(report: CBox_PublicGetReportListItemData, type: CBox_PublicReportFileTypes): number {
    return report.files.filter(file => file.isGenerated && file.type === type).length;
  }

  getReportVariationLabel(report: CBox_PublicGetReportListItemData, fileType: CBox_PublicReportFileTypes, variation: string): string {
    const translations = this.reportVariationsTranslations()?.[report.type]?.[fileType];
    return translations?.[variation] || variation;
  }

  getReportMenuItems(report: CBox_PublicGetReportListItemData, fileType: CBox_PublicReportFileTypes): MenuItem[] {
    const menuFileTypes = report.files.filter(file => file.type === fileType);

    const fileTypeItems: MenuItem[] = menuFileTypes.map(file => ({
      label: this.getReportVariationLabel(report, file.type, file.variation.name),
      icon: file.type === this.reportFileTypes.PDF ? "pi pi-file-pdf" : "pi pi-file-excel",
      command: () => this.downloadReport(report, fileType, file.variation.name)
    }));

    return fileTypeItems;
  }

  getUniqueFileTypes(report: CBox_PublicGetReportListItemData): CBox_PublicGetReportListItemData["files"] {
    const uniqueMap = new Map<string, typeof report.files[number]>();

    report.files
      .filter(file => file.isGenerated)
      .forEach(file => {
        if (!uniqueMap.has(file.type)) {
          uniqueMap.set(file.type, file);
        }
      });

    return Array.from(uniqueMap.values());
  }

  generateReport(): void {
    const dialog = this.dialog.open(CBoxProfileReportGenerateControllerComponent, {
      width: "min(600px, 100%)",
      disableClose: true,
      autoFocus: false
    });

    dialog.afterClosed().pipe(first()).subscribe((reportType: "lockers" | "agents" | "shipments" | "pickUpTimesActivity" | "lowActivity") => {
      let dialog;
      if (reportType) {
        switch (reportType) {
          case "agents": {
            dialog = this.dialog.open(CBoxProfileAgentsActivityReportGenerateDialogComponent, {
              width: "min(600px, 100%)",
              data: {
                interval: this.filtersForm.value.interval
              },
              disableClose: true,
            });
            break;
          }

          case "lockers": {
            dialog = this.dialog.open(CBoxProfileLockersActivityReportGenerateDialogComponent, {
              width: "min(600px, 100%)",
              data: {
                interval: this.filtersForm.value.interval
              },
              disableClose: true,
            });
            break;
          }

          case "shipments": {
            dialog = this.dialog.open(CBoxProfileShipmentActivityReportGenerateDialogComponent, {
              width: "min(600px, 100%)",
              data: {
                interval: this.filtersForm.value.interval
              },
              disableClose: true,
            });
            break;
          }

          case "pickUpTimesActivity": {
            dialog = this.dialog.open(CBoxProfilePickUpTimesActivityReportGenerateDialogComponent, {
              width: "min(600px, 100%)",
              data: {
                interval: this.filtersForm.value.interval
              },
              disableClose: true,
            });
            break;
          }

          case "lowActivity": {
            dialog = this.dialog.open(CBoxProfileLockerLowActivityReportGenerateDialogComponent, {
              width: "min(600px, 100%)",
              data: {
                interval: this.filtersForm.value.interval
              },
              disableClose: true,
            });
            break;
          }
        }
      }
      dialog?.afterClosed().pipe(first()).subscribe(() => {
        this.loading.set(true);
        this.fetchData();
      });
    });
  }

  override async fetchData(): Promise<void> {
    try {
      const reports = await this.reportsService.getPaginatedReports(this.getSearchStructure());
      this.data.set(reports.data);
      this.totalCount.set(reports.totalCount);
      this.loading.set(false);
    } catch (e: any) {
      this.reportsService.retryFetchingActiveReports();
      handlePublicApiError(e, this.toastService);
      this.loading.set(false);
    }
  }

  getSearchStructure() {
    let interval = this.filtersForm.value.interval;
    if (typeof interval === "string") {
      interval = interval.split(",");
    }

    return Object.entries({
      "lockers[]": this.filtersForm.value.lockers,
      "agents[]": this.filtersForm.value.agents,
      page: +this.filtersForm.value.page,
      pageSize: +this.filtersForm.value.pageSize,
      startDate: interval[0] ? new Date(new Date(interval[0]).setHours(0)).toISOString() : this.oneMonthAgo,
      endDate: interval[1] ? new Date(new Date(interval[1]).setHours(23, 59, 59, 599)).toISOString() : this.today,
      "types[]": this.filtersForm.value.types,
    })
    .filter(([_, value]) => value?.length || !!value)
    .reduce((acc, [key, value]) => {
      acc[key as keyof CBox_PublicGetReportListDataParams] = value;
      return acc;
    }, {} as any);
  }

  openLockerDetails(lockerName: string): void {
    this.dialog.open(CBoxProfileLockerDataComponent, {
      data: lockerName,
      minWidth: "min(1200px, 100%)",
    });
  }
}